import { Component, Host, h, Prop, State, Event, EventEmitter, Watch, Method } from '@stencil/core';
import { localizeNumbers } from '../../../utils/utils';
import state from './../../../state/cta-price';
import visualState from './../../../state/store';

@Component({
  tag: 'afs-cta-price-button',
  styleUrl: 'afs-cta-price-button.sass',
  shadow: true,
})

export class AfsCtaPriceButton {

  @Prop() variant : "card" | "simple" = "card";

  @Prop() price : number = 10;
  @Prop() ticket : number = 50;
  @Prop() ticketLimit : number = 10;
  @Prop() ticketUnit : string = this.ticket === 1 ? "ENTRY" : "ENTRIES";
  @Prop() hrefFragment : string = "";
  @Prop() contrastBackgroundColor : string = "#222222";
  @Prop() contrastColor : string = "#FFFFFF";
  @Prop() backgroundColor : string = "coral";
  @Prop() color : string = "teal";
  @Prop() label : string;
  @Prop() labelColor : string = "#FFF";
  @Prop() labelBackgroundColor : string = "teal"
  @Prop() soldOut : boolean = false;

  @Prop() langPreposition : string = 'FOR';
  @Prop() langQuantity : string = 'QTY';
  @Prop() langTotal : string = 'TOTAL';
  @Prop() langSoldOut : string = 'SOLD OUT';

  @Prop() initialQuantity : number = 0;

  @State() currentButtonIndex : number;
  @State() quantity : number = 0;
  @State() total : number = this.price * this.quantity;
  
  @State() quantityExceeded: number | null = null;
  @State() numbersExceeded: number | null = null;

  @Method()
  async getPricePointInfo() {
    return {
        currentButtonIndex: this.currentButtonIndex,
        quantity: this.quantity,
        ticket: this.ticket,
        price: this.price,
        totalTicket: this.ticket * this.quantity,
        totalPrice: this.total,
        hrefFragment: this.hrefFragment,
    }
  }

  @Event() pricePointChange: EventEmitter;
  pricePointChangeHandler(currentQuantity = this.quantity, oldQuantity = this.quantity) {
    const quantityDifference = currentQuantity - oldQuantity;

    this.pricePointChange.emit({
      quantity: currentQuantity,
      ticket: this.ticket,
      totalTicket: currentQuantity * this.ticket,
      totalPrice: currentQuantity * this.price,
      quantityDifference,
      totalTicketDifference: quantityDifference * this.ticket,
      totalPriceDifference: quantityDifference * this.price
    });
  }

  @Watch('quantity')
  function (newVal: number, oldVal: number) {
    this.pricePointChangeHandler(newVal, oldVal);
  }

  componentDidLoad() {
    this.pricePointChangeHandler(this.quantity, 0);
  }
  
  /**
   * Before Load, component will update the shared state
   */
  componentWillLoad() {
    // Ensure initial quantity does not result to total entries being larger than 5000
    if (this.initialQuantity > 0 && state.CTATotalEntries + this.initialQuantity * this.ticket <= state.CTANumbersLimit) {
        this.quantity = this.initialQuantity;
    }

    this.currentButtonIndex = state.CTAPriceButtonStatus.length; // index is inclusive, length is exclusive
    state.CTAPriceButtonStatus = [ ...state.CTAPriceButtonStatus, this.buildStatusObject()];
    this.total = this.price * state.CTAPriceButtonStatus[this.currentButtonIndex].quantity;

    if (this.currentButtonIndex > 0 && this.label) {
        visualState.requireLabelPadding = true;
    }
  }


  /**
   * Update the quantity in the shared state
   * @param method
   */
  modQuantity(method : 'add' | 'subtract') {

    state.CTAPriceButtonStatus.splice(this.currentButtonIndex, 1);

    switch (method) {
      case 'add' :
        this.quantityExceeded = this.quantity + 1 > this.ticketLimit ? this.ticketLimit : null;
        this.numbersExceeded = state.CTATotalEntries + this.ticket > state.CTANumbersLimit ? state.CTANumbersLimit : null;

        if (this.quantityExceeded === null && this.numbersExceeded === null) {
          this.quantity++;
        }
        break;
      case 'subtract' :
        this.quantity = this.quantity !== 0 ? this.quantity - 1 : 0;
        this.quantityExceeded = null;
        this.numbersExceeded = null;
        break;
    }

    state.CTAPriceButtonStatus = [ ...state.CTAPriceButtonStatus, this.buildStatusObject()];
  }

  /**
   * A function that returns the base structure of a single ticket
   */
  buildStatusObject() {
    return {
      index: this.currentButtonIndex,
      price: this.price,
      ticket: this.ticket,
      quantity: this.quantity,
      hrefFragment: this.hrefFragment,
      quantityExceeded: this.quantityExceeded,
      numbersExceeded: this.numbersExceeded,
    }
  }

  render() {
    return (
      <Host
        style={{
          width: this.variant === 'card' ? '13rem' : '100%',
          margin: this.variant === 'card' ? '0.5rem 1rem' : '0',
        }}
      >
        <div 
          class={`button-wrapper ${this.soldOut ? 'sold-out' : ''} variant-${this.variant}`}
          style={{
            '--background-color': this.backgroundColor,
            '--contrast-background-color': this.contrastBackgroundColor,
            '--contrast-color': this.contrastColor,
            '--color': this.color,
            '--label-padding': (this.label || visualState.requireLabelPadding) ? '0.6rem' : '0',
            '--content-sold-out': `'${this.langSoldOut}'`,
          }}  
        >
          {(this.label || this.soldOut) && this.variant === "card" && <div class="label" style={{
            color: this.labelColor,
            backgroundColor: this.labelBackgroundColor,
          }}>
            <span>{this.soldOut ? this.langSoldOut : this.label}</span>
          </div>}
          <div class="price">
            <span class="desc">{ this.variant === 'card' ? localizeNumbers(this.price, true) : this.ticket } { this.langPreposition } </span>
            <span class="ticket-value value">
              { this.variant === 'card' ? this.ticket : localizeNumbers(this.price, true) }
            </span>
            {
              this.variant === 'card' && <span class="desc">{this.ticketUnit}</span>
            }
          </div>
          <div class="operator">
            <button class="plus" onClick={() => this.modQuantity('add') }>&#43;</button>
            {
              this.variant !== 'card' && <span class="operator-quantity">{state.CTAPriceButtonStatus[this.currentButtonIndex].quantity}</span>
            }
            <button class="minus" onClick={() => this.modQuantity('subtract') }>&#45;</button>
          </div>
          {
            this.variant === 'card' &&
            <div class="quantity">
              <span class="desc">{ this.langQuantity }</span>
              <span class="quantity-value value">{state.CTAPriceButtonStatus[this.currentButtonIndex].quantity}</span>
            </div>
          }
          {
            this.variant === 'card' &&
            <div class="total">
              <span class="desc">{ this.langTotal }</span>
              <span class="total-value value">{localizeNumbers(state.CTAPriceButtonStatus[this.currentButtonIndex].quantity * this.price, true)}</span>
            </div>
          }
        </div>
      </Host>
    );
  }

}
